import { CardColors, CardFooter, CardFooterItem } from "$components/card";
import {
  CheckIcon as RawCheckIcon,
  InvestigationIcon as RawInvestigationIcon,
} from "$components/icons";
import type { InvestigationActivity } from "$global-providers/rxdb-provider/collections/investigation-activity";
import type { PaymentStatus } from "$routes/billing/_lib/pre-service/payment-status";
import { PaymentStatusBadge } from "$routes/billing/_lib/pre-service/payment-status-badge";
import {
  CardHeader,
  PatientHistoryCard,
} from "$routes/patient/_lib/history-card";
import RawRequestedBubble from "$routes/patient/[:patientId]/_lib/investigations/requested-bubble";
import { styled } from "@material-ui/core";
import { format } from "date-fns";
import { FormattedMessage } from "react-intl";
import { BinButton } from "./bin-button";
import type { TranslatedInvestigationCatalogueItem } from "./util";

const RequestedBubble = styled(RawRequestedBubble)(() => ({
  marginRight: "10px",
}));

const CheckIcon = styled(RawCheckIcon)(({ theme }) => ({
  backgroundColor: theme.palette.primary.main,
  borderRadius: "20px",
  padding: "5px",
  color: "#ffffff",
}));

const InvestigationIcon = styled(RawInvestigationIcon)(({ theme }) => ({
  marginRight: theme.spacing(1),
  color: theme.palette.blue.main,
}));

const BodyWrapper = styled("div")(({ theme }) => ({
  marginTop: theme.spacing(2),
}));

export const InvestigationCard: FCwC<{
  investigationId: string;
  catalogueItem: TranslatedInvestigationCatalogueItem;
  onDelete?: () => void;
  request?: InvestigationActivity["request"];
  outcome?: InvestigationActivity["outcome"];
  isValid?: boolean;
  isTouched?: boolean;
  canEdit?: boolean;
  paymentStatus?: PaymentStatus | null;
}> = ({
  investigationId,
  catalogueItem,
  request,
  outcome,
  isValid,
  onDelete,
  children,
  isTouched,
  canEdit = true,
  paymentStatus = null,
}) => {
  const isRequested = !outcome && !isTouched;
  const Header = () => (
    <CardHeader
      color={CardColors.INVESTIGATION}
      category="Investigation"
      title={catalogueItem.title}
      icon={<InvestigationIcon />}
      id={investigationId}
      extras={
        <>
          <PaymentStatusBadge status={paymentStatus} />
          {isRequested && (
            <RequestedBubble>
              <strong>
                <FormattedMessage
                  id="investigationCard.requested"
                  defaultMessage="Requested"
                />
              </strong>
            </RequestedBubble>
          )}
          {isValid && !isRequested && <CheckIcon />}
          {onDelete != null ? (
            <BinButton aria-label="delete-investigation" onClick={onDelete} />
          ) : null}
        </>
      }
    />
  );

  const formatPerformer = (name: string | undefined, date: Date) =>
    `${name ? `${name}, ` : ""}${format(date, "d MMM yyyy")} ${format(date, "HH:mm")}`;

  const Footer = () => {
    if (!request?.meta?.created && !outcome?.meta?.updated) return null;

    return (
      <CardFooter>
        {request?.meta?.created && (
          <CardFooterItem
            label={
              <FormattedMessage
                id="investigationActivity.request.performer"
                defaultMessage="Requested by:"
              />
            }
          >
            {formatPerformer(
              request.performer?.name,
              new Date(request.meta.created),
            )}
          </CardFooterItem>
        )}
        {outcome?.meta?.updated && (
          <CardFooterItem
            label={
              <FormattedMessage
                id="investigationActivity.outcome.performer"
                defaultMessage="Submitted by:"
              />
            }
          >
            {formatPerformer(
              outcome.performer?.name,
              new Date(outcome.meta.updated),
            )}
          </CardFooterItem>
        )}
      </CardFooter>
    );
  };
  return (
    <PatientHistoryCard
      id={`investigationCard${investigationId}`}
      color={CardColors.INVESTIGATION}
      defaultExpanded={isRequested}
      HeaderComponent={Header}
      FooterComponent={Footer}
    >
      <BodyWrapper>{children}</BodyWrapper>
    </PatientHistoryCard>
  );
};
